import dynamic from "next/dynamic";
const App = dynamic(() => import("../../components/App"));
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import Link from "next/link";
import Head from "next/head";
import { useRouter } from "next/router";
import axios from "axios";
import {
  Breadcrumb,
  InputNumber,
  Spin,
  Typography,
  Row,
  Col,
  Button,
  Collapse,
  Slider,
  Checkbox,
  Radio,
  Modal,
  Empty,
  Dropdown,
  Space,
} from "antd";
import { DownOutlined, UpOutlined, SmileOutlined } from "@ant-design/icons";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import InfiniteScroll from "react-infinite-scroller";
import { CheckOutlined } from "@ant-design/icons";
import NextImage from "next/image";

import { Api } from "../../networks/Api";
import { getItemWithExpiry } from "../../utils/localstorage";
const CardComponent = dynamic(() => import("../../components/Card"));
const CheckboxGroup = Checkbox.Group;
const { Panel } = Collapse;
const { Title, Text } = Typography;

import ThumborJS from "thumborjs";

const imageUrl = process.env.NEXT_PUBLIC_IMAGE_URL;
const thumborKey = process.env.NEXT_PUBLIC_THUMBOR_KEY;
const thumborUrl = process.env.NEXT_PUBLIC_THUMBOR_URL;

const SSR_PAGE_SIZE = 10;
const FIXMENU = ["recommend", "new", "best", "sale", "campaign", "live"];

// สร้าง URL ดึงสินค้าฝั่ง server ให้ตรง logic กับ buildUrl ฝั่ง client
const buildServerProductUrl = ({
  pageNum,
  category,
  query,
  brand_array = [],
  category_array = [],
}) => {
  const {
    cate,
    filtercate,
    subcate,
    brand,
    brand_code,
    size_code,
    color_code,
    min_price,
    max_price,
    search,
    sortby,
    filter_gender,
  } = query;

  let url = `${Api.getFeedProductList}?page=${pageNum}&size=${SSR_PAGE_SIZE}`;

  if (FIXMENU.includes(category)) url = `${url}&type=${category}`;
  if (category_array.includes(category))
    url = `${url}&group_category=${category}`;

  let brandlist;
  if (brand_code) {
    brandlist = brand_code
      .split(",")
      .map((b) => encodeURIComponent(b.trim()))
      .join(",");
  }

  if (brand_array.includes(category) && !brand_code)
    url = `${url}&brand_code=${encodeURIComponent(category.trim())}`;

  if (cate) url = `${url}&group_category_code=${cate}`;
  if (filtercate) url = `${url}&group_category_code=${filtercate}`;
  if (subcate) url = `${url}&category_code=${subcate}`;
  if (brand) url = `${url}&brand_number=${brand}`;
  if (brand_code) url = `${url}&brand_code=${brandlist.trim()}`;
  if (size_code) url = `${url}&size_code=${size_code}`;
  if (color_code) url = `${url}&color_code=${color_code}`;
  if (min_price) url = `${url}&min_price=${min_price}`;
  if (max_price) url = `${url}&max_price=${max_price}`;
  if (search) url = `${url}&search=${search}`;
  if (sortby) url = `${url}&sortby=${sortby}`;
  if (filter_gender) url = `${url}&filter_gender=${filter_gender}`;

  if (
    category != "all" &&
    !brand_array.includes(category) &&
    !category_array.includes(category) &&
    !FIXMENU.includes(category)
  )
    url = `${url}&brand_code=${encodeURIComponent(category.trim())}`;

  return url;
};

export async function getServerSideProps(ctx) {
  let brand_array = [];
  let category_array = [];
  let article_data = {};
  let initialBrandData = {}; // map: number -> name (ใช้สร้าง href ของ Card ฝั่ง server)
  let initialItems = [];
  let initialPages = 1;
  let initialCurrentPage = 1;
  let initialCategoryData = {};
  let initialPromotion = [];
  let initialLive = [];
  let initialLiveDescription = {};
  let initialPromotionDetail = null;
  let initialSubCategoryData = {};
  let initialFeedData = null;

  const category = ctx.params.category;
  const ssrPage = Math.max(1, parseInt(ctx.query?.page, 10) || 1);

  try {
    // ยิง brand + category + article พร้อมกัน (parallel) ลด server response time
    const [brandRes, categoryRes, articleRes] = await Promise.allSettled([
      axios.get(`${Api.getFeedBrandList}`, Api.Header()),
      axios.get(`${Api.getFeedCategoryList}`, Api.Header()),
      axios.post(
        `${Api.getFeedArticleByBrand}`,
        { brand_name: category },
        Api.Header(),
      ),
    ]);

    if (brandRes.status === "fulfilled") {
      const brandRows = brandRes.value?.data?.data || [];
      brand_array = brandRows.map((v) => v.name);
      initialBrandData = brandRows.reduce((acc, v) => {
        acc[v.number] = v.name;
        return acc;
      }, {});
    }

    if (categoryRes.status === "fulfilled") {
      const categoryRows = categoryRes.value?.data?.data || [];
      category_array = categoryRows.map((v) => v.name_en);
      initialCategoryData = categoryRows.reduce((acc, v) => {
        acc[v.number] = v.name_en;
        return acc;
      }, {});
    }

    if (articleRes.status === "fulfilled")
      article_data = articleRes.value?.data?.data || {};
  } catch (error) {}

  // ดึงสินค้าหน้าแรก (หรือหน้าที่ระบุใน ?page=) ฝั่ง server เพื่อให้อยู่ใน HTML แรก
  try {
    const productUrl = buildServerProductUrl({
      pageNum: ssrPage,
      category,
      query: ctx.query || {},
      brand_array,
      category_array,
    });
    const productRes = await axios.get(productUrl, Api.Header());
    if (productRes?.status == 200) {
      const productData = productRes.data || {};
      initialPromotion = productData.promotions || [];

      if (!productData.promotions) {
        // รองรับทั้ง key dataWithColor และ data (บาง query เช่นหน้าแบรนด์อาจคืนคนละ key)
        initialItems = productData.dataWithColor || productData.data || [];
        initialPages = productData.pages || 1;
        initialCurrentPage = productData.currentPage || ssrPage;
        initialLive = productData.lives || [];
        initialLiveDescription = productData.live_description || {};
        initialPromotionDetail = productData.descriptions || null;
        initialFeedData = {
          data: {
            currency_min: productData.currency_min || null,
            currency_max: productData.currency_max || null,
          },
        };

        (productData.sub_category || []).forEach((v) => {
          initialSubCategoryData[
            String(v?.group_category_code) + String(v?.number)
          ] = v?.name;
        });
      }
    }
  } catch (error) {}

  return {
    props: {
      ...(await serverSideTranslations(ctx?.locale, ["common"])),
      token: ctx?.req?.cookies?.token || null,
      category,
      locale: ctx?.locale,
      brand_array,
      category_array,
      article_data,
      initialItems,
      initialBrandData,
      initialCategoryData,
      initialPages,
      initialCurrentPage,
      initialPromotion,
      initialLive,
      initialLiveDescription,
      initialPromotionDetail,
      initialSubCategoryData,
      initialFeedData,
    },
  };
}

const Home = ({
  token,
  category,
  locale,
  brand_array,
  category_array,
  article_data,
  initialItems,
  initialBrandData,
  initialCategoryData,
  initialPages,
  initialCurrentPage,
  initialPromotion,
  initialLive,
  initialLiveDescription,
  initialPromotionDetail,
  initialSubCategoryData,
  initialFeedData,
  categoryMenu,
  hasSale,
  hasCampaign,
  hasLive,
  dataSeo,
  dataReferrer,
}) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const {
    cate,
    filtercate,
    filter_gender,
    subcate,
    brand,
    brand_code,
    size_code,
    color_code,
    min_price,
    max_price,
    type,
    search,
    sortby,
  } = router.query;
  let queryParam = router?.query?.aid ? `aid=${router?.query?.aid}` : ``;
  queryParam = router?.query?.pid
    ? `${queryParam}&pid=${router?.query?.pid}`
    : queryParam;

  queryParam = router?.query?.pf
    ? `${queryParam}pf=${router?.query?.pf}`
    : queryParam;

  if (router?.query?.utm_source) {
    queryParam = queryParam
      ? `${queryParam}&utm_source=${router?.query?.utm_source}`
      : `?utm_source=${router?.query?.utm_source}`;
  }

  const [fetching, setFetching] = useState(false);
  // ใช้ข้อมูลจาก SSR ต่อบน client เพื่อไม่ต้องล้างแล้วโหลด page แรกซ้ำ
  const [items, setItems] = useState(initialItems || []);
  const [currentPage, setCurrentPage] = useState(initialCurrentPage || 1);

  const [hasMoreItems, setHasMoreItems] = useState(
    !(Array.isArray(initialPromotion)
      ? initialPromotion.length
      : initialPromotion) && (initialCurrentPage || 1) < (initialPages || 1),
  );
  // React state อัปเดตแบบ async จึงใช้ ref เป็น lock เพื่อกัน InfiniteScroll
  // เรียก loadMore ซ้ำก่อน fetching จะถูก render เป็น true
  const fetchingRef = useRef(false);
  const hasMoreItemsRef = useRef(hasMoreItems);
  const nextPageRef = useRef((initialCurrentPage || 1) + 1);
  // seed brand map (number -> name) เพื่อให้ href ของ Card ถูกต้องตั้งแต่ฝั่ง server
  const [brandData, setBrandData] = useState(initialBrandData || []);
  const [selectBrand, setSelectBrand] = useState(brand_array || []);
  const [brandList, setBrandList] = useState([]);
  const [categoryGenderList, setCategoryGenderList] = useState([
    { value: "for-men", label: "Men" },
    { value: "for-women", label: "Women" },
    { value: "for-kids", label: "Kids" },
  ]);
  const [categoryGenderValue, setCategoryGenderValue] = useState(filter_gender);
  const [priceList, setPriceList] = useState([]);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(1000000);
  const [colorData, setColorData] = useState([]);
  const [selectColor, setSelectColor] = useState([]);
  const [feedData, setFeedData] = useState(initialFeedData);
  const [categoryDetail, setCategoryDetail] = useState([]);
  const [categoryData, setCategoryData] = useState(initialCategoryData || {});
  const [subCategoryData, setSubCategoryData] = useState(
    initialSubCategoryData || {},
  );
  const [promotion, setPromotion] = useState(initialPromotion || []);
  const [promotionDetail, setPromotionDetail] = useState(
    initialPromotionDetail,
  );
  const [live, setLive] = useState(initialLive || []);
  const [liveDescription, setLiveDescription] = useState(
    initialLiveDescription || {},
  );
  const query_ref = useRef(router.asPath);
  const [open, setOpen] = useState(false);
  const [discountAll, setDiscountAll] = useState({});
  const [valueSort, setValueSort] = useState("");
  const [readmore, setReadmore] = useState(false);
  // mounted: กัน placeholder (Loading/Product not found) โผล่ใน HTML ฝั่ง server
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // pagination แบบ crawlable จากจำนวนหน้าที่ SSR รู้
  const currentSsrPage = initialCurrentPage || 1;
  const totalSsrPages = initialPages || 1;
  const buildPageUrl = (pageNum) => {
    const params = new URLSearchParams();
    Object.entries(router.query || {}).forEach(([k, v]) => {
      if (k !== "category" && k !== "page" && v != null && v !== "")
        params.set(k, String(v));
    });
    if (pageNum > 1) params.set("page", String(pageNum));
    const qs = params.toString();
    return `/${encodeURIComponent(category)}${qs ? `?${qs}` : ""}`;
  };

  const { currencyData } = useSelector((state) => state?.currency);

  const sort_items = [
    {
      key: 0,
      sort_key: "latest_new",
      label: t(`latest_new`),
    },
    {
      key: 1,
      sort_key: "price_low_to_high",
      label: t(`price_low_to_high`),
    },
    {
      key: 2,
      sort_key: "price_high_to_low",
      label: t(`price_high_to_low`),
    },
  ];
  const onClick = ({ key }) => {
    setValueSort(key);
    onFilter(`sortby`, key);
  };

  useEffect(() => {
    const onRouteChangeStart = () => {
      const { query } = router;
      window.localStorage.setItem("itemPrev", JSON.stringify(items));
      window.localStorage.setItem("itemPage", currentPage);
      window.localStorage.setItem("scrollPositions", window.scrollY);
      window.localStorage.setItem("categoryPrev", query.category);
    };

    router.events.on("routeChangeStart", onRouteChangeStart);
    // router.events.on("routeChangeComplete", onRouteChangeComplete);

    return () => {
      router.events.off("routeChangeStart", onRouteChangeStart);
      // router.events.off("routeChangeComplete", onRouteChangeComplete);
    };
  }, [router, items, currentPage]);

  useEffect(() => {
    const itemPage = window.localStorage.getItem("itemPage");
    const itemPrev = window.localStorage.getItem("itemPrev");
    const categoryPrev = window.localStorage.getItem("categoryPrev"); // ดึงมาเช็คกรณีเปลี่ยนหน้าจาก Bags ไป Footwear ไม่มีต้อง set ค่า prev
    // const productPrev = window.localStorage.getItem("productPrev"); // ดึง storage ที่เก็บในหน้าสินค้ามาเช็ค ถ้าไม่มีค่านี้ไม่ต้อง set ค่า prev
    const productPrev = getItemWithExpiry("productPrev"); // ดึง storage ที่เก็บในหน้าสินค้ามาเช็ค ถ้าไม่มีค่านี้ไม่ต้อง set ค่า prev
    if (router.query.category == categoryPrev && productPrev) {
      const prevCurrentPage = parseInt(itemPage, 10);

      // ตอน Back จากหน้ารายละเอียด ให้คืนรายการและ page จาก cache ทันที
      // โดยไม่รอ InfiniteScroll แล้วยิง API page 1..หน้าที่เคยดูซ้ำทั้งหมด
      if (
        Number.isFinite(prevCurrentPage) &&
        itemPrev &&
        currentPage !== prevCurrentPage
      ) {
        try {
          const previousItems = JSON.parse(itemPrev);
          if (Array.isArray(previousItems)) {
            setItems(previousItems);
            setCurrentPage(prevCurrentPage);
            nextPageRef.current = prevCurrentPage + 1;

            const restoredHasMore = prevCurrentPage < (initialPages || 1);
            hasMoreItemsRef.current = restoredHasMore;
            setHasMoreItems(restoredHasMore);
            return;
          }
        } catch (error) {
          console.error("Unable to restore product feed:", error);
        }
      }

      if (currentPage === prevCurrentPage) {
        const scrollPositions = window.localStorage.getItem("scrollPositions");
        if (scrollPositions) {
          const product_detail = document.getElementById("product_detail");
          product_detail
            ? (product_detail.style.minHeight = `${scrollPositions}px`)
            : null;

          window.scroll({
            top: parseInt(scrollPositions),
            behavior: "auto",
          });
          window.localStorage.removeItem("itemPrev");
          window.localStorage.removeItem("itemPage");
          window.localStorage.removeItem("scrollPositions");
          window.localStorage.removeItem("categoryPrev");
          window.localStorage.removeItem("productPrev");
        }
      }
    }
  }, [currentPage, initialPages, router]);

  useEffect(() => {
    if (query_ref.current === router.asPath) return;

    setFetching(false);
    fetchingRef.current = false;
    setItems(initialItems || []);
    setCurrentPage(initialCurrentPage || 1);
    nextPageRef.current = (initialCurrentPage || 1) + 1;
    const nextHasMore =
      !(Array.isArray(initialPromotion)
        ? initialPromotion.length
        : initialPromotion) && (initialCurrentPage || 1) < (initialPages || 1);
    hasMoreItemsRef.current = nextHasMore;
    setHasMoreItems(nextHasMore);
    setPromotion(initialPromotion || []);
    setPromotionDetail(initialPromotionDetail);
    setLive(initialLive || []);
    setLiveDescription(initialLiveDescription || {});
    setFeedData(initialFeedData);
    setBrandData(initialBrandData || {});
    setSelectBrand(brand_array || []);
    setCategoryData(initialCategoryData || {});
    setSubCategoryData(initialSubCategoryData || {});

    const categoryPrev = window.localStorage.getItem("categoryPrev");
    if (router.query.category != categoryPrev) {
      window.localStorage.removeItem("itemPrev");
      window.localStorage.removeItem("itemPage");
      window.localStorage.removeItem("scrollPositions");
      window.localStorage.removeItem("categoryPrev");
      window.localStorage.removeItem("productPrev");
    }
    const product_detail = document.getElementById("product_detail");
    product_detail ? (product_detail.style.minHeight = `auto`) : null;
    query_ref.current = router.asPath;
  }, [
    router.asPath,
    router.query.category,
    initialItems,
    initialCurrentPage,
    initialPages,
    initialPromotion,
    initialPromotionDetail,
    initialLive,
    initialLiveDescription,
    initialFeedData,
    initialBrandData,
    initialCategoryData,
    initialSubCategoryData,
    brand_array,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      const results = await Promise.allSettled([
        axios.get(Api.webpromotionAll, Api.Header()),
        axios.get(Api.getColorList, Api.Header()),
        axios.get(`${Api.getGroupCategory}/${category}`, Api.Header()),
      ]);

      const [promotionResult, colorResult, categoryDetailResult] = results;

      if (promotionResult.status === "fulfilled") {
        setDiscountAll(promotionResult.value?.data?.data);
      } else {
        console.error("webpromotionAll error:", promotionResult.reason);
      }

      if (colorResult.status === "fulfilled") {
        setColorData(colorResult.value?.data?.data);
      } else {
        console.error("getColorList error:", colorResult.reason);
      }

      if (categoryDetailResult.status === "fulfilled") {
        setCategoryDetail(categoryDetailResult.value?.data?.data);
      } else {
        console.error("getGroupCategory error:", categoryDetailResult.reason);
      }
    };

    fetchData();

    setBrandList(brand_code?.split(",") || []);
    setSelectColor(color_code);
  }, [brand_code, color_code, category]);

  useEffect(() => {
    if (feedData) {
      const res_min_price =
        feedData?.data?.currency_min?.currency[currencyData]?.price;
      const res_max_price =
        feedData?.data?.currency_max?.currency[currencyData]?.price;

      const c_min_price = res_min_price;
      const c_max_price = res_max_price;
      // setMinPrice(c_min_price);
      // setMaxPrice(c_max_price);
      if (min_price && max_price) setPriceList([min_price, max_price]);
      else setPriceList([c_min_price, c_max_price]);
    }
  }, [feedData, currencyData, min_price, max_price]);

  const onFilter = (index, value) => {
    let url = type ? `/${category}?type=${type}` : `/${category}?type=filter`;

    if (subcate && index != "subcate" && subcate != undefined)
      url = `${url}&subcate=${subcate}`;

    if (brandList) {
      const formattedBrands = brandList
        .map((brand) => encodeURIComponent(brand.trim())) // แปลงค่าภายใน array
        .join(",");
      url = `${url}&brand_code=${formattedBrands}`;
    }

    if (brand) url = `${url}&brand=${encodeURIComponent(brand)}`;

    if (size_code && index != "size_code" && size_code != undefined)
      url = `${url}&size_code=${size_code}`;

    if (selectColor && selectColor != undefined)
      url = `${url}&color_code=${selectColor}`;

    url = `${url}&min_price=${priceList[0]}&max_price=${priceList[1]}`;

    if (search && index != "search") url = `${url}&search=${search}`;

    if (categoryGenderValue)
      url = `${url}&filter_gender=${categoryGenderValue}`;

    if (index == "sortby") {
      url = `${url}&${index}=${sort_items[value]?.sort_key}`;
    } else if (value) url = `${url}&${index}=${value}`;

    if (queryParam) url = `${url}${queryParam ? `&${queryParam}` : ``}`;

    setOpen(false);
    router.push(url);
  };

  const myLoader = ({ src, width, quality }) => {
    if (src.split("?")?.length > 1) {
      return `${src}`;
    } else {
      const tugen = new ThumborJS(thumborUrl, thumborKey);
      const url = tugen.imgpath(src).resize(width, 0).genurl();

      return url;
    }
  };

  const onChangeBrand = (list) => {
    setBrandList(list);
  };

  const onChangePrice = (values) => {
    setPriceList(values);
  };

  const onChangePriceMin = (values) => {
    let price = [values, priceList[1]];
    setPriceList(price);
  };

  const onChangePriceMax = (values) => {
    let price = [priceList[0], values];
    setPriceList(price);
  };

  const onChangeColor = (values) => {
    setSelectColor(values.target.value);
  };

  const fetchItems = useCallback(async () => {
    if (!hasMoreItemsRef.current || fetchingRef.current) return;

    fetchingRef.current = true;
    setFetching(true);
    const page = nextPageRef.current;
    const size = 10;

    // สร้าง URL base (ไม่มี page)
    const buildUrl = (pageNum) => {
      let url = `${Api.getFeedProductList}?page=${pageNum}&size=${size}`;
      if (FIXMENU.includes(category)) url = `${url}&type=${category}`;
      if (category_array.includes(category))
        url = `${url}&group_category=${category}`;

      let brandlist;
      if (brand_code) {
        const newbrand = brand_code.split(`,`);
        brandlist = newbrand
          .map((brand) => encodeURIComponent(brand.trim()))
          .join(",");
      }

      if (brand_array.includes(category) && !brand_code)
        url = `${url}&brand_code=${encodeURIComponent(category.trim())}`;

      if (cate) url = `${url}&group_category_code=${cate}`;
      if (filtercate) url = `${url}&group_category_code=${filtercate}`;
      if (subcate) url = `${url}&category_code=${subcate}`;
      if (brand) url = `${url}&brand_number=${brand}`;
      if (brand_code) url = `${url}&brand_code=${brandlist.trim()}`;
      if (size_code) url = `${url}&size_code=${size_code}`;
      if (color_code) url = `${url}&color_code=${color_code}`;
      if (min_price) url = `${url}&min_price=${min_price}`;
      if (max_price) url = `${url}&max_price=${max_price}`;
      if (search) url = `${url}&search=${search}`;
      if (sortby) url = `${url}&sortby=${sortby}`;
      if (filter_gender) url = `${url}&filter_gender=${filter_gender}`;

      if (
        category != "all" &&
        !brand_array.includes(category) &&
        !category_array.includes(category) &&
        !FIXMENU.includes(category)
      )
        url = `${url}&brand_code=${encodeURIComponent(category.trim())}`;

      return url;
    };

    try {
      const lastResponse = await axios.get(buildUrl(page), Api.Header());

      if (lastResponse?.status == 200) {
        if (lastResponse?.data?.promotions) {
          setPromotion(lastResponse.data.promotions);
          hasMoreItemsRef.current = false;
          setHasMoreItems(false);
        } else {
          const mergedData = lastResponse?.data?.dataWithColor ?? [];

          const pages = Number(lastResponse?.data?.pages) || 1;
          const res_currentPage =
            Number(lastResponse?.data?.currentPage) || page;

          setLive(lastResponse?.data?.lives);
          setLiveDescription(lastResponse?.data?.live_description);
          setPromotionDetail(lastResponse?.data?.descriptions);
          setCurrentPage(res_currentPage);
          nextPageRef.current = res_currentPage + 1;
          setFeedData(lastResponse);

          let sub_category = lastResponse?.data?.sub_category;
          let result = [];
          if (sub_category) {
            sub_category?.map((v) => {
              result[String(v?.group_category_code) + String(v?.number)] =
                v?.name;
            });
          }
          setSubCategoryData(result);

          const hasNextPage = mergedData.length > 0 && res_currentPage < pages;
          hasMoreItemsRef.current = hasNextPage;
          setHasMoreItems(hasNextPage);

          // page แรก = แทนที่ (กันข้อมูลเบิ้ลจาก items ที่ seed ไว้ตอน SSR),
          // page ถัดไป = ต่อท้ายตามปกติ
          if (res_currentPage == 1) setItems(mergedData);
          else setItems((previousItems) => [...previousItems, ...mergedData]);
        }
      } else {
        hasMoreItemsRef.current = false;
        setHasMoreItems(false);
      }
    } catch (e) {
      console.log("fetchData >> getCartList error :", e);
      hasMoreItemsRef.current = false;
      setHasMoreItems(false);
    } finally {
      fetchingRef.current = false;
      setFetching(false);
    }
  }, [
    brand,
    brand_code,
    cate,
    filtercate,
    filter_gender,
    subcate,
    search,
    size_code,
    color_code,
    min_price,
    max_price,
    sortby,
    category,
    brand_array,
    category_array,
  ]);

  const itemLoader = (
    <div key="loader" className="loader">
      Loading ...
    </div>
  );

  let title = "Products";
  if (category) title = category;
  if (category == "recommend") title = "Recommended Products";
  if (category == "new") title = "New Arrivals";
  if (category == "best") title = "Best Seller";
  if (category == "sale") title = "Sale";
  if (category == "campaign") title = "Campaign";
  if (category == "live") title = "Live Promotion";
  if (
    ["Bags", "Clothing", "Footwear", "Accessories", "Jewelries"].includes(
      category,
    )
  ) {
    title = category;
  }
  if (cate) title = categoryData[cate];
  if (subcate) title = subcate;
  if (brand_code && type != "filter") title = brand_code;
  if (search) title = search;

  return (
    <App
      token={token}
      categoryMenu={categoryMenu}
      hasSale={hasSale}
      hasLive={hasLive}
      dataSeo={dataSeo}
      dataReferrer={dataReferrer}
    >
      {/* <Head>
        <title>{title}</title>
        <meta name="description" content={categoryDetail?.description_th} />
      </Head> */}
      <Head>
        {currentSsrPage > 1 && (
          <link rel="prev" href={buildPageUrl(currentSsrPage - 1)} />
        )}
        {currentSsrPage < totalSsrPages && (
          <link rel="next" href={buildPageUrl(currentSsrPage + 1)} />
        )}
      </Head>
      <Spin spinning={fetching}>
        <Row justify="center" style={{ marginBlock: 24 }}>
          <Col xs={22} sm={22} md={22} lg={22} xl={20}>
            <Breadcrumb
              items={[
                {
                  key: "home",
                  title: (
                    <Link href={`/${queryParam ? `?${queryParam}` : ``}`}>
                      {t("home")}
                    </Link>
                  ),
                },
                {
                  key: "category",
                  title: title,
                },
              ]}
            />
          </Col>
        </Row>

        <Modal
          className="modal-menu"
          title={`${t("filter")}`}
          centered
          open={open}
          onOk={() => setOpen(false)}
          onCancel={() => setOpen(false)}
          footer={
            <Button
              className="btn-login"
              block
              type="primary"
              htmlType="submit"
              onClick={() => onFilter("brand", "")}
            >
              {t("submit")}
            </Button>
          }
        >
          {!brand_array?.includes(category) && (
            <Collapse ghost className="inside-filter-brand">
              <Panel header={t("brand")} key="1">
                <CheckboxGroup
                  className="checkbox-brand"
                  options={selectBrand}
                  value={brandList}
                  onChange={onChangeBrand}
                />
              </Panel>
            </Collapse>
          )}

          <Collapse ghost className="inside-filter">
            <Panel header={t("color")} key="1">
              <Radio.Group
                className="select-color"
                onChange={onChangeColor}
                value={selectColor}
              >
                {colorData?.map((v) => {
                  return (
                    <Radio key={v?.number} value={v?.number}>
                      <div className="select-color-container">
                        <div className="select-color-radio">
                          <div
                            className="color"
                            style={{
                              backgroundColor: `${v?.color_code || "#f6f6f6"}`,
                            }}
                          ></div>
                          <CheckOutlined />
                        </div>
                        <Text>{v?.name}</Text>
                      </div>
                    </Radio>
                  );
                })}
              </Radio.Group>
            </Panel>
          </Collapse>
          <Collapse ghost className="inside-filter">
            <Panel header={t("price")} key="1">
              <Row>
                <Col align="left" xs={12} style={{ paddingRight: 15 }}>
                  <InputNumber
                    style={{ width: "100%" }}
                    max={maxPrice}
                    min={minPrice}
                    value={priceList[0]}
                    onChange={(e) => {
                      onChangePriceMin(e);
                    }}
                  />
                </Col>
                <Col align="right" xs={12} style={{ paddingleft: 15 }}>
                  <InputNumber
                    style={{ width: "100%" }}
                    max={maxPrice}
                    min={minPrice}
                    value={priceList[1]}
                    onChange={onChangePriceMax}
                  />
                </Col>
              </Row>
              <Slider
                range={{ draggableTrack: true }}
                value={priceList}
                max={maxPrice}
                min={minPrice}
                onChange={onChangePrice}
              />
            </Panel>
          </Collapse>
        </Modal>

        {promotion?.website_image_path && (
          <Row justify="center">
            <Col xs={23} sm={23} md={23} lg={23} xl={20}>
              <Row justify="center">
                <Col xs={0} sm={24} md={24} lg={24} xl={24}>
                  <div className="img-169">
                    <NextImage
                      loader={myLoader}
                      src={`${imageUrl}${promotion?.website_image_path?.replace(
                        /uploads/g,
                        "",
                      )}`}
                      alt="Image"
                      layout="fill"
                      objectFit="contain"
                      className="img-custom"
                      quality={20}
                    />
                  </div>
                </Col>
                <Col xs={24} sm={0} md={0} lg={0} xl={0}>
                  <div className="img-169">
                    <NextImage
                      loader={myLoader}
                      src={`${imageUrl}${promotion?.mobile_image_path?.replace(
                        /uploads/g,
                        "",
                      )}`}
                      alt="Image"
                      layout="fill"
                      objectFit="contain"
                      className="img-custom"
                      quality={20}
                    />
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        )}

        {live?.website_image_path && (
          <Row justify="center">
            <Col xs={23} sm={23} md={23} lg={23} xl={20}>
              <Row justify="center">
                <Col xs={0} sm={24} md={24} lg={24} xl={24}>
                  <div className="img-169">
                    <NextImage
                      loader={myLoader}
                      src={`${imageUrl}${live?.website_image_path?.replace(
                        /uploads/g,
                        "",
                      )}`}
                      alt="Image"
                      layout="fill"
                      objectFit="contain"
                      className="img-custom"
                      quality={20}
                    />
                  </div>
                </Col>
                <Col xs={24} sm={0} md={0} lg={0} xl={0}>
                  <div className="img-169">
                    <NextImage
                      loader={myLoader}
                      src={`${imageUrl}${live?.mobile_image_path?.replace(
                        /uploads/g,
                        "",
                      )}`}
                      alt="Image"
                      layout="fill"
                      objectFit="contain"
                      className="img-custom"
                      quality={20}
                    />
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        )}

        {promotion?.length == 0 && (
          <Row justify="center" style={{ marginBlock: 24 }}>
            <Col xs={22} sm={22} md={0} lg={0} xl={0}>
              <Button
                className="btn-login-outline mb-2"
                onClick={() => {
                  setOpen(true);
                }}
              >
                {t("filter")}
              </Button>
            </Col>
            <Col xs={0} sm={0} md={7} lg={5} xl={4}>
              <p className="h5">{t("filter")}</p>
              {!brand_array.includes(category) && (
                <Collapse ghost className="inside-filter-brand">
                  <Panel header={t("brand")} key="1">
                    <CheckboxGroup
                      className="checkbox-brand"
                      options={selectBrand}
                      value={brandList}
                      onChange={onChangeBrand}
                    />
                  </Panel>
                </Collapse>
              )}
              <Collapse ghost className="inside-filter">
                <Panel header={t("color")} key="1">
                  <Radio.Group
                    className="select-color"
                    onChange={onChangeColor}
                    value={selectColor}
                  >
                    {colorData?.map((v) => {
                      return (
                        <Radio key={v?.number} value={v?.number}>
                          <div className="select-color-container">
                            <div className="select-color-radio">
                              <div
                                className="color"
                                style={{
                                  backgroundColor: `${
                                    v?.color_code || "#f6f6f6"
                                  }`,
                                }}
                              ></div>
                              <CheckOutlined />
                            </div>
                            <Text>{v?.name}</Text>
                          </div>
                        </Radio>
                      );
                    })}
                  </Radio.Group>
                </Panel>
              </Collapse>
              <Collapse ghost className="inside-filter">
                <Panel header={t("price")} key="1">
                  <Row>
                    <Col align="left" xs={12} style={{ paddingRight: 15 }}>
                      <InputNumber
                        style={{ width: "100%" }}
                        max={maxPrice}
                        min={minPrice}
                        value={priceList[0]}
                        onChange={(e) => {
                          onChangePriceMin(e);
                        }}
                      />
                    </Col>
                    <Col align="right" xs={12} style={{ paddingleft: 15 }}>
                      <InputNumber
                        style={{ width: "100%" }}
                        max={maxPrice}
                        min={minPrice}
                        value={priceList[1]}
                        onChange={onChangePriceMax}
                      />
                    </Col>
                  </Row>
                  <Slider
                    range={{ draggableTrack: true }}
                    value={priceList}
                    max={maxPrice}
                    min={minPrice}
                    onChange={onChangePrice}
                  />
                </Panel>
              </Collapse>

              <CheckboxGroup
                className="checkbox-brand"
                style={{ marginLeft: "15px" }}
                options={categoryGenderList}
                value={categoryGenderValue}
                onChange={(e) => {
                  console.log(e[0]);
                  setCategoryGenderValue(e[0]);
                }}
              />

              <Button
                className="btn-login"
                block
                type="primary"
                htmlType="submit"
                onClick={() => onFilter("brand", "")}
              >
                {t("submit")}
              </Button>
            </Col>
            <Col id="product_detail" xs={22} sm={22} md={15} lg={17} xl={16}>
              <p
                className="h4"
                style={{ marginBottom: "0.5rem", fontSize: "28px" }}
              >
                {title}
              </p>
              <p
                style={{
                  marginBottom: "2rem",
                  lineHeight: "1.1rem",
                  color: "rgba(0, 0, 0, 0.65)",
                  letterSpacing: "0.04rem",
                  textIndent: "2rem",
                }}
              >
                {(category == "sale" || category == "campaign") &&
                !promotionDetail
                  ? `สินค้าราคาพิเศษ สินค้าแรร์ไอเท็ม ขนขบวนกันมาลดราคา สูงสุด 50%`
                  : promotionDetail && locale == "th"
                    ? promotionDetail?.description_th
                    : promotionDetail && locale == "en"
                      ? promotionDetail?.description_en
                      : category == "live" && locale == "th"
                        ? liveDescription?.description_th
                        : category == "live" && locale == "en"
                          ? liveDescription?.description_en
                          : locale == "th"
                            ? categoryDetail?.description_th
                            : locale == "en"
                              ? categoryDetail?.description_en
                              : ``}
              </p>

              <Row justify="end" style={{ marginTop: 24, marginBottom: 24 }}>
                <Col xs={24} align="right">
                  <Dropdown
                    menu={{
                      items: sort_items,
                      selectable: true,
                      onClick,
                      defaultSelectedKeys: [valueSort],
                    }}
                    trigger={["click"]}
                  >
                    <a
                      onClick={(e) => e.preventDefault()}
                      style={{ color: `black` }}
                    >
                      <Space>
                        {t(`sortby`)} : {sort_items[valueSort]?.label || ``}
                        <DownOutlined />
                      </Space>
                    </a>
                  </Dropdown>
                </Col>
              </Row>

              {mounted && items?.length == 0 && (
                <Row
                  style={{ marginTop: 24, marginBottom: 24 }}
                  justify="center"
                >
                  {/* Detail */}
                  <Col xs={24} sm={24} md={10} lg={10} xl={8}>
                    <Row justify={"center"}>
                      <Col>
                        <Empty description={`${t("no-product")}`} />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              )}

              <InfiniteScroll
                pageStart={currentPage}
                loadMore={fetchItems}
                hasMore={mounted && hasMoreItems && !fetching}
                initialLoad={false}
                loader={itemLoader}
              >
                <Row>
                  {items?.map((product) => {
                    return (
                      <Col
                        xs={12}
                        sm={12}
                        md={8}
                        lg={6}
                        xl={6}
                        key={`title-${product?.id}`}
                      >
                        <CardComponent
                          product={product}
                          t={t}
                          categoryData={categoryData}
                          subCategoryData={subCategoryData}
                          discountAll={discountAll}
                          brandData={brandData}
                        />
                      </Col>
                    );
                  })}
                </Row>
              </InfiniteScroll>

              {/* ลิงก์ pagination แบบ crawlable (ให้ Google เดินไปหน้า 2+ ได้ ไม่พึ่ง infinite scroll) */}
              {totalSsrPages > 1 && (
                <nav
                  aria-label="Pagination"
                  aria-hidden="true"
                  hidden
                  style={{ display: "none" }}
                >
                  {currentSsrPage > 1 && (
                    <Link href={buildPageUrl(currentSsrPage - 1)}>
                      ‹ Previous
                    </Link>
                  )}
                  {currentSsrPage < totalSsrPages && (
                    <Link href={buildPageUrl(currentSsrPage + 1)}>Next ›</Link>
                  )}
                </nav>
              )}

              {article_data?.description && (
                <>
                  <div
                    className={`article-content article-content-readmore ${
                      readmore ? "show" : "hide"
                    }`}
                    dangerouslySetInnerHTML={{
                      __html:
                        locale == "th"
                          ? article_data?.description
                          : article_data?.description_en,
                    }}
                  />
                  <div
                    className="readmore"
                    onClick={() => {
                      setReadmore(!readmore);
                    }}
                  >
                    {readmore ? (
                      <>
                        {t("show-less")} <UpOutlined />
                      </>
                    ) : (
                      <>
                        {t("show-more")} <DownOutlined />
                      </>
                    )}
                  </div>
                </>
              )}
            </Col>
          </Row>
        )}
      </Spin>
    </App>
  );

  return <div></div>;
};

export default Home;
